import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import SEO from "@/components/seo/SEO";
import {
  GoldButton, GhostButton, Eyebrow, SectionHeading, GlassCard, AmbientOrbs,
} from "@/components/common/ui";
import { Cpu, Sparkle, ShieldCheck, Buildings, ChartLineUp, Robot, Bed, ForkKnife, ChatText, Sliders } from "@phosphor-icons/react";

export default function BhtOne() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "en";
  const modules = t("modules.list", { returnObjects: true }) || [];
  const aiCaps = t("ai.caps", { returnObjects: true }) || [];
  const pillars = t("platform.pillars", { returnObjects: true }) || [];

  return (
    <>
      <SEO title={lang === "nl" ? "BHT One — Het Hospitality Operating System" : "BHT One — The Hospitality Operating System"} path="/bht-one" />

      <section className="relative overflow-hidden py-32 lg:py-44" data-testid="bhtone-hero">
        <AmbientOrbs />
        <div className="mx-auto grid max-w-[1400px] gap-12 px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-8">
            <Eyebrow>BHT · One</Eyebrow>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="mt-8 font-serif text-5xl leading-[1.05] tracking-tight sm:text-6xl md:text-7xl"
            >
              {lang === "nl" ? "Één platform. " : "One platform. "}
              <span className="italic text-bht-gold">{lang === "nl" ? "Achttien modules." : "Eighteen modules."}</span>{" "}
              {lang === "nl" ? "Een besturingssysteem voor uw hotel." : "An operating system for your hotel."}
            </motion.h1>
            <p className="mt-8 max-w-2xl text-lg text-white/65">{t("home.section_platform_sub")}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <GoldButton to={`/${lang}/book-demo`} testId="bhtone-cta-demo">{t("cta.primary")}</GoldButton>
              <GhostButton to={`/${lang}/platform`} testId="bhtone-cta-platform">{t("nav.platform")} →</GhostButton>
            </div>
          </div>
        </div>
      </section>

      {/* Bento grid overview */}
      <section className="relative border-y border-white/[0.06] py-24 lg:py-32" data-testid="bhtone-overview">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid gap-4 md:grid-cols-4 md:grid-rows-3">
            <div className="glass rounded-2xl p-8 md:col-span-2 md:row-span-2">
              <div className="eyebrow">Data model</div>
              <div className="mt-6 font-serif text-3xl leading-tight">One canonical schema for the entire hotel.</div>
              <p className="mt-4 text-sm text-white/55">Reservations · Guests · Rooms · Staff · Revenue · Inventory. One source of truth, event-streamed to every module and integration.</p>
              <div className="mt-8 grid grid-cols-3 gap-2">
                {[
                  { k: "Guests", v: "1.2M" },
                  { k: "Events/day", v: "48M" },
                  { k: "Latency", v: "< 200ms" },
                ].map((s, i) => (
                  <div key={i} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
                    <div className="font-serif text-2xl">{s.v}</div>
                    <div className="eyebrow mt-1 text-[9px]">{s.k}</div>
                  </div>
                ))}
              </div>
            </div>
            <GlassCard className="md:col-span-2"><Cpu size={22} className="text-bht-gold" weight="thin" /><div className="mt-4 font-serif text-xl">Event-driven core</div><p className="mt-2 text-sm text-white/55">Real-time state, no polling, deterministic replays.</p></GlassCard>
            <GlassCard><Sparkle size={22} className="text-bht-gold" weight="thin" /><div className="mt-4 font-serif text-lg">AI substrate</div></GlassCard>
            <GlassCard><ShieldCheck size={22} className="text-bht-gold" weight="thin" /><div className="mt-4 font-serif text-lg">Zero-trust</div></GlassCard>
            <GlassCard className="md:col-span-2"><Buildings size={22} className="text-bht-gold" weight="thin" /><div className="mt-4 font-serif text-xl">Multi-property, multi-region</div><p className="mt-2 text-sm text-white/55">Data residency in EU · US · APAC.</p></GlassCard>
            <GlassCard><Robot size={22} className="text-bht-gold" weight="thin" /><div className="mt-4 font-serif text-lg">Open APIs</div></GlassCard>
            <GlassCard><ChartLineUp size={22} className="text-bht-gold" weight="thin" /><div className="mt-4 font-serif text-lg">Insights</div></GlassCard>
          </div>
        </div>
      </section>

      {/* Modules */}
      <section className="relative py-32 lg:py-44">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <SectionHeading eyebrow={t("modules.eyebrow")} title={t("modules.h1")} sub={t("modules.sub")} />
          <div className="mt-16 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {modules.map((m, i) => {
              const Icon = { reception: Bed, housekeeping: Sparkle, restaurant: ForkKnife, "ai-copilot": Robot, analytics: ChartLineUp }[m.slug] || ChatText;
              return (
                <Link
                  key={m.slug}
                  to={`/${lang}/modules/${m.slug}`}
                  className="group flex flex-col rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8 transition-colors hover:border-bht-gold/30"
                  data-testid={`bhtone-module-${m.slug}`}
                >
                  <div className="flex items-center justify-between">
                    <Icon size={24} weight="thin" className="text-bht-gold" />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-white/30">M/{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <h3 className="mt-8 font-serif text-2xl group-hover:text-bht-gold">{m.title}</h3>
                  <p className="mt-3 text-sm text-white/55">{m.desc}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* AI Capabilities */}
      <section className="relative border-t border-white/[0.06] bg-bht-navy2/30 py-32 lg:py-44">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <SectionHeading eyebrow={t("ai.eyebrow")} title={t("ai.h1")} />
          <div className="mt-16 grid gap-5 md:grid-cols-2 lg:grid-cols-5">
            {aiCaps.map((c, i) => (
              <GlassCard key={c.title} className="p-6" testId={`bhtone-ai-${i}`}>
                <div className="font-mono text-[10px] uppercase tracking-widest text-bht-gold">AI/{String(i + 1).padStart(2, "0")}</div>
                <div className="mt-4 font-serif text-xl">{c.title}</div>
                <p className="mt-2 text-sm text-white/55">{c.body}</p>
              </GlassCard>
            ))}
          </div>
        </div>
      </section>

      {/* Pillars quick nav */}
      <section className="relative py-32 lg:py-44">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <SectionHeading eyebrow={t("platform.eyebrow")} title={t("platform.h1")} sub={t("platform.sub")} />
          <div className="mt-16 grid gap-4 md:grid-cols-3">
            {pillars.map((p, i) => (
              <div key={p.title} className="group rounded-2xl border border-white/[0.06] p-6 transition-colors hover:border-bht-gold/30">
                <div className="font-mono text-[10px] uppercase tracking-widest text-bht-gold">P/{String(i + 1).padStart(2, "0")}</div>
                <div className="mt-4 font-serif text-xl">{p.title}</div>
                <p className="mt-2 text-sm text-white/55">{p.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-16"><GhostButton to={`/${lang}/platform`}>{t("nav.platform")} →</GhostButton></div>
        </div>
      </section>
    </>
  );
}
